import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getStudentDetail,
  resetStudentPassword,
  type ChatMessage,
  type Student,
  type StudentProjectDetail,
} from '../api/teacher';
import { AppLayout } from '../components/AppLayout';
import { BigButton, LoadingDots } from '../components/ChildFriendly';

const EVIDENCE_LABELS: Record<string, { label: string; icon: string }> = {
  anket: { label: 'Anket / Veri', icon: '📊' },
  foto:  { label: 'Fotoğraf',     icon: '📸' },
  ses:   { label: 'Ses Kaydı',    icon: '🎤' },
  video: { label: 'Video',        icon: '🎥' },
  cizim: { label: 'Çizim / Şema', icon: '✏️' },
  metin: { label: 'Metin',        icon: '📝' },
  yok:   { label: 'Kanıt Yok',    icon: '❓' },
};

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('tr-TR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '?').slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export default function StudentDetailPage() {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const [student, setStudent] = useState<Student | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [projects, setProjects] = useState<StudentProjectDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Expanded project chat IDs state
  const [expandedProjectIds, setExpandedProjectIds] = useState<number[]>([]);

  // Password reset modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [customPassword, setCustomPassword] = useState('');
  const [passwordSuccessMsg, setPasswordSuccessMsg] = useState<string | null>(null);
  const [passwordErrorMsg, setPasswordErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true);
    getStudentDetail(Number(studentId))
      .then((data) => {
        if (!cancelled) {
          setStudent(data.student);
          setMessages(data.messages);
          setProjects(data.projects || []);
          // Auto expand first project if available
          if (data.projects && data.projects.length > 0) {
            setExpandedProjectIds([data.projects[0].id]);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Öğrenci bilgisi yüklenemedi.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [studentId]);

  function toggleExpandProject(projectId: number) {
    setExpandedProjectIds((prev) =>
      prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId],
    );
  }

  async function handleUpdatePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!student) return;
    setPasswordErrorMsg(null);
    setPasswordSuccessMsg(null);
    try {
      const res = await resetStudentPassword(student.id, customPassword.trim() || undefined);
      setPasswordSuccessMsg(`Şifre güncellendi: ${res.temp_password}`);
      setCustomPassword('');
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccessMsg(null);
      }, 2000);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setPasswordErrorMsg(detail || 'Şifre güncellenemedi.');
    }
  }

  function handleGenerateRandomPassword() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#';
    let rand = '';
    for (let i = 0; i < 10; i++) {
      rand += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCustomPassword(rand);
  }

  if (loading) {
    return (
      <AppLayout title="Öğrenci Detayı">
        <LoadingDots message="Öğrenci bilgileri yükleniyor" />
      </AppLayout>
    );
  }

  if (error || !student) {
    return (
      <AppLayout title="Öğrenci Detayı">
        <div role="alert" className="error" style={{ marginBottom: 16 }}>
          {error || 'Öğrenci bulunamadı.'}
        </div>
        <BigButton variant="ghost" onClick={() => navigate('/teacher')}>
          ← Öğretmen paneline dön
        </BigButton>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Öğrenci Detay Portfolyosu"
      subtitle="Öğrencinin tüm projelerini, YZ diyaloglarını inceleyin ve şifresini yönetin."
      width="wide"
      actions={
        <BigButton variant="ghost" onClick={() => navigate('/teacher')}>
          ← Öğretmen paneline dön
        </BigButton>
      }
    >
      <div className="stack">
        {/* Student Header Info Card */}
        <div className="student-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span className="student-header__avatar" aria-hidden="true">
              {getInitials(student.display_name)}
            </span>
            <div className="student-header__info">
              <h2 className="student-header__name" style={{ margin: 0 }}>{student.display_name}</h2>
              <div className="student-header__sub">
                @{student.username} · {student.classroom_name}
              </div>
            </div>
          </div>
          <div>
            <button
              type="button"
              className="btn btn--primary btn--md"
              onClick={() => {
                setShowPasswordModal(true);
                setCustomPassword('');
                setPasswordSuccessMsg(null);
                setPasswordErrorMsg(null);
              }}
            >
              🔑 Şifre Değiştir
            </button>
          </div>
        </div>

        {/* Section 1: Projects Breakdown & Chat Inspection */}
        <section className="stack" aria-labelledby="projects-heading">
          <h3 id="projects-heading" className="section-title">
            🚀 Projeleri ({projects.length})
          </h3>
          {projects.length === 0 ? (
            <div className="card" style={{ padding: '1rem', color: '#6b7280' }}>Henüz açılmış bir proje yok.</div>
          ) : (
            <div className="stack">
              {projects.map((p) => {
                const isExpanded = expandedProjectIds.includes(p.id);
                const projectMsgs = messages.filter((m) => m.project_id === p.id);

                return (
                  <div key={p.id} className="card stack" style={{ borderLeft: '5px solid #4f46e5', padding: '1.25rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
                      <div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.2rem' }}>
                          Durum: <span className={`status-badge status-${p.status}`}>{statusLabel(p.status)}</span> · Oluşturulma: {new Date(p.created_at).toLocaleDateString('tr-TR')}
                        </div>
                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.15rem', fontWeight: 700, color: '#1f2937' }}>
                          "{p.problem_text}"
                        </h4>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        {p.average_score !== null && p.average_score !== undefined && (
                          <span style={{ fontWeight: 700, background: '#e0e7ff', color: '#3730a3', padding: '6px 12px', borderRadius: 8, fontSize: '0.95rem' }}>
                            Ortalama ⭐ {p.average_score}
                          </span>
                        )}
                        {p.diploma_pdf && (
                          <a href={p.diploma_pdf} target="_blank" rel="noreferrer" className="btn btn--secondary btn--sm">
                            🎓 Mezuniyet Diploması (PDF) ↗
                          </a>
                        )}
                        <button
                          type="button"
                          className="btn btn--primary btn--sm"
                          onClick={() => navigate(`/projects/${p.id}`)}
                        >
                          🌐 Öğrenci Arayüzüyle İncele ( /projects/{p.id} )
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          style={{ fontWeight: 600, color: '#4f46e5', border: '1px solid #c7d2fe' }}
                          onClick={() => toggleExpandProject(p.id)}
                        >
                          💬 YZ Konuşmalarını {isExpanded ? 'Gizle ▲' : `İncele (${projectMsgs.length}) ▼`}
                        </button>
                      </div>
                    </div>

                    {/* Role Sessions */}
                    <div style={{ marginTop: '0.5rem', background: '#f8fafc', padding: '0.75rem', borderRadius: 8, border: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.5rem', color: '#4b5563' }}>
                        Rol Simülasyon Adımları ({p.role_sessions.length}):
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
                        {p.role_sessions.map((r) => (
                          <div
                            key={r.id}
                            style={{ background: '#ffffff', border: '1px solid #cbd5e1', padding: '0.6rem 0.8rem', borderRadius: 8, fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.2s' }}
                            onClick={() => navigate(`/projects/${p.id}/roles/${r.id}`)}
                            title="Tıkla ve bu rol konuşmasını öğrenci arayüzüyle incele"
                          >
                            <div style={{ fontWeight: 700, color: '#1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>{r.order}. {r.role_name}</span>
                              <span style={{ fontSize: '0.75rem', color: '#4f46e5' }}>İncele ↗</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.3rem', color: '#6b7280', fontSize: '0.75rem' }}>
                              <span>Durum: <strong>{statusLabel(r.status)}</strong></span>
                              <span>Puan: <strong>{r.score ?? '—'}</strong></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>


                    {/* Expandable Chat Thread for this Project */}
                    {isExpanded && (
                      <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px dashed #e2e8f0' }}>
                        <h5 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 700, color: '#334155' }}>
                          💬 Bu Projenin YZ Konuşma Detayları ({projectMsgs.length} mesaj)
                        </h5>

                        {projectMsgs.length === 0 ? (
                          <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.9rem' }}>
                            Bu projede henüz mesaj bulunmuyor.
                          </div>
                        ) : (
                          <div className="chat-thread" style={{ gap: '0.75rem' }}>
                            {projectMsgs.map((m) => {
                              const evidenceMeta = m.evidence_type ? EVIDENCE_LABELS[m.evidence_type] : null;
                              return (
                                <article
                                  key={m.id}
                                  className={`chat-msg is-${m.sender}`}
                                  style={{ marginBottom: '0.75rem' }}
                                >
                                  <div className="chat-msg__meta">
                                    <span className="chat-msg__role">
                                      {m.sender === 'student' ? '🧒 Öğrenci' : '🤖 YZ Kişisi'}
                                    </span>
                                    <span>·</span>
                                    <strong>{m.role_name}</strong>
                                    <span>·</span>
                                    <time dateTime={m.created_at}>{formatTime(m.created_at)}</time>
                                    {evidenceMeta && m.sender === 'student' && (
                                      <>
                                        <span>·</span>
                                        <span>{evidenceMeta.icon} {evidenceMeta.label}</span>
                                      </>
                                    )}
                                  </div>
                                  <p className="chat-msg__content" style={{ whiteSpace: 'pre-wrap' }}>{m.content}</p>
                                  {m.attached_file && (
                                    <div className="chat-msg__attachment" style={{ marginTop: '0.4rem' }}>
                                      📎 <a href={m.attached_file} target="_blank" rel="noreferrer">Ek dosyayı aç ↗</a>
                                    </div>
                                  )}
                                </article>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="det-pwd-title">
          <div className="modal-content">
            <div className="modal-header">
              <h3 id="det-pwd-title">🔑 Şifre Güncelle</h3>
              <button type="button" className="modal-close" onClick={() => setShowPasswordModal(false)}>×</button>
            </div>
            <p style={{ fontSize: '0.95rem', color: '#4b5563', marginBottom: '1rem' }}>
              <strong>{student.display_name}</strong> için yeni bir şifre belirleyin.
            </p>

            {passwordSuccessMsg && (
              <div role="status" style={{ background: '#dcfce7', color: '#166534', padding: '0.75rem', borderRadius: 8, marginBottom: '1rem' }}>
                ✅ {passwordSuccessMsg}
              </div>
            )}
            {passwordErrorMsg && (
              <div role="alert" className="error" style={{ marginBottom: '1rem' }}>
                ⚠️ {passwordErrorMsg}
              </div>
            )}

            <form onSubmit={handleUpdatePassword} className="stack">
              <div className="form-field">
                <label htmlFor="student_det_pwd" className="form-label">Özel Şifre Belirleyin</label>
                <div className="password-input-group">
                  <input
                    id="student_det_pwd"
                    type="text"
                    className="form-input"
                    placeholder="Örn. YeniSifre2026!"
                    value={customPassword}
                    onChange={(e) => setCustomPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={handleGenerateRandomPassword}
                  >
                    🎲 Rastgele
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
                <button type="button" className="btn btn--ghost btn--md" onClick={() => setShowPasswordModal(false)}>İptal</button>
                <button type="submit" className="btn btn--primary btn--md">Şifreyi Güncelle</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

function statusLabel(status: string): string {
  switch (status) {
    case 'draft': return 'Taslak';
    case 'in_progress': return 'Devam ediyor';
    case 'pending': return 'Bekliyor';
    case 'passed': return 'Onaylandı (Geçti)';
    case 'failed': return 'Reddedildi';
    case 'no_project': return 'Proje yok';
    default: return status;
  }
}
